import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl, api_service_path } from "./utils";

export const settingAction = createApi({
    reducerPath: "settingApi",
    baseQuery: fetchBaseQuery({
        prepareHeaders: (headers) => {
            const current_user = localStorage.getItem("current_user") ? JSON.parse(localStorage.getItem("current_user")) : null
            if (current_user?.token) {
                headers.set(
                    "Authorization",
                    `Bearer ${current_user.token}`
                )
            }
            return headers
        },
        baseUrl: `${baseUrl}/${api_service_path.setting}`,
    }),
    tagTypes: ["Platform", "ClientList", "PlatformFee", "DatabaseTables"],
    endpoints: (builder) => ({
        createPlatform: builder.mutation({
            query: ({ name, logo, state, district, pin, address }) => ({
                url: `/create`,
                method: "POST",
                body: {
                    name,
                    logo,
                    state,
                    district,
                    pin,
                    address
                },
            }),
            invalidatesTags: ["platformCreate", "ClientList"]
        }),
        updateInstitutionToUserAccount: builder.mutation({
            query: ({ email, meta_data }) => ({
                url: "/update_user_platform",
                method: "PUT",
                body: {
                    email,
                    meta_data
                }
            }),
            invalidatesTags: ["addInstitutionToUserAccount"]
        }),
        getUserInstitution: builder.query({
            query: (id) => ({
                url: `/single/${id}`,
                method: "GET"
            }),
            invalidatesTags: ["userInstitution"]
        }),
        createInstitutionMetadata: builder.mutation({
            query: ({ platform_id, meta_data }) => ({
                url: "/create_metadata",
                method: "POST",
                body: {
                    platform_id,
                    meta_data
                }
            }),
            invalidatesTags: ["institutionMetadata"]
        }),
        updateInstitutionMetadata: builder.mutation({
            query: ({ platform_id, meta_data }) => ({
                url: "/update_metadata",
                method: "PUT",
                body: {
                    platform_id,
                    meta_data
                }
            }),
            invalidatesTags: ["updateInstitutionMetadata"]
        }),
        getInstitutionMetadata: builder.query({
            query: (id) => ({
                url: `/institution_metadata/${id}`,
                method: "GET"
            })
        }),
        updateInstitution: builder.mutation({
            query: ({ id, data }) => ({
                url: `/edit/${id}`,
                method: "PUT",
                body: {
                    ...data
                }
            })
        }),
        createPlatformRoles: builder.mutation({
            query: ({ platform_id, designation, meta_data }) => ({
                url: "/create_platform_role",
                method: "POST",
                body: {
                    platform_id,
                    designation,
                    meta_data
                }
            })
        }),
        getPlatformRoles: builder.query({
            query: ({ platform_id }) => ({
                url: `/platform_roles/${platform_id}`,
                method: "GET"
            })
        }),
        createPlatformUser: builder.mutation({
            query: ({ firstname, lastname, email, password, meta_data }) => ({
                url: "/create_platform_user",
                method: "POST",
                body: {
                    firstname,
                    lastname,
                    email,
                    password,
                    meta_data
                }
            })
        }),
        getPlatformUserList: builder.query({
            query: ({ platform_id }) => ({
                url: `/platform_users/${platform_id}`,
                method: "GET"
            })
        }),
        updateUserMetadata: builder.mutation({
            query: ({ userRef, userInfo }) => ({
                url: `/update_user_metadata/${userRef}`,
                method: "PUT",
                body: userInfo
            })
        }),
        deleteUserFromInstitution: builder.mutation({
            query: ({ user_ref }) => ({
                url: `/delete_platfrom_user/${user_ref}`,
                method: "DELETE"
            })
        }),
        createPlatformFee: builder.mutation({
            query: ({
                fee_name,
                fee_amount
            }) => ({
                url: `/create_platform_fee`,
                params: { fee_name, fee_amount },
                method: "POST"
            }),
            invalidatesTags: ["PlatformFee"]
        }),
        editPlatformFee: builder.mutation({
            query: ({
                fee_ref,
                fee_name,
                fee_amount
            }) => ({
                url: `/edit_fee/${fee_ref}`,
                params: { fee_name, fee_amount },
                method: "PUT"
            }),
            invalidatesTags: ["PlatformFee"]
        }),
        deletePlatformFee: builder.mutation({
            query: ({
                fee_ref
            }) => ({
                url: `/remove_fee/${fee_ref}`,
                method: "DELETE"
            }),
            invalidatesTags: ["PlatformFee"]
        }),
        getAllFee: builder.query({
            query: () => ({
                url: "/all_fee",
                method: "GET"
            }),
            providesTags: ["PlatformFee"]
        }),
        useGetAllPlatformsList: builder.query({
            query: () => ({
                url: "/all",
                method: "GET"
            })
        }),
        // Owner only
        getClientList: builder.query({
            query: () => ({
                url: "/clients",
                method: "GET"
            }),
            providesTags: ["ClientList"]
        }),
        // Owner only; billable user counts per client
        getBilling: builder.query({
            query: () => ({
                url: "/billing",
                method: "GET"
            }),
            providesTags: ["ClientList"]
        }),
        // Owner only; expiry_date is "YYYY-MM-DD" or null to remove it
        updateClientExpiry: builder.mutation({
            query: ({ client_id, expiry_date }) => ({
                url: `/clients/${client_id}/expiry`,
                method: "PUT",
                body: { expiry_date }
            }),
            invalidatesTags: ["ClientList"]
        }),
        // Owner only; counts of everything a client delete will remove
        getClientDeletePreview: builder.query({
            query: (client_id) => ({
                url: `/clients/${client_id}/delete_preview`,
                method: "GET"
            }),
            keepUnusedDataFor: 0
        }),
        // Owner only; permanently deletes the client and all of its data
        deleteClient: builder.mutation({
            query: ({ client_id, confirm_name }) => ({
                url: `/clients/${client_id}`,
                method: "DELETE",
                params: { confirm_name }
            }),
            invalidatesTags: ["ClientList"]
        }),
        // Owner only; every table in the database with its row count and columns
        getDatabaseTables: builder.query({
            query: () => ({
                url: "/database/tables",
                method: "GET"
            }),
            providesTags: ["DatabaseTables"]
        }),
        // Owner only; one page of a table's rows, optionally filtered by search
        getDatabaseTableRows: builder.query({
            query: ({ table_name, page, page_size, search }) => ({
                url: `/database/tables/${encodeURIComponent(table_name)}`,
                method: "GET",
                params: { page, page_size, search }
            }),
            providesTags: ["DatabaseTables"]
        }),
        // Owner only; table_name omitted exports every table. Resolves to a Blob
        downloadDatabaseExport: builder.mutation({
            query: ({ table_name, format }) => ({
                url: table_name
                    ? `/database/tables/${encodeURIComponent(table_name)}/export`
                    : "/database/export",
                method: "GET",
                params: { format },
                // Errors come back as JSON, so only successful responses are read as a file
                responseHandler: (response) => response.ok ? response.blob() : response.json()
            })
        }),
        // Owner only; table_name omitted imports a whole-database export.
        // dry_run checks the file without saving; mode is insert, skip or upsert
        importDatabaseData: builder.mutation({
            query: ({ table_name, file, mode, dry_run }) => {
                const body = new FormData()
                body.append("file", file)
                return {
                    url: table_name
                        ? `/database/tables/${encodeURIComponent(table_name)}/import`
                        : "/database/import",
                    method: "POST",
                    params: { mode, dry_run },
                    body
                }
            },
            invalidatesTags: (result, error, { dry_run }) =>
                !dry_run && result && result.ok ? ["DatabaseTables"] : []
        })
    })
})

export const {
    useCreatePlatformMutation,
    useGetAllPlatformsQuery,
    useUpdateInstitutionToUserAccountMutation,
    useGetUserInstitutionQuery,
    useLazyGetUserInstitutionQuery,
    useCreateInstitutionMetadataMutation,
    useGetInstitutionMetadataQuery,
    useLazyGetInstitutionMetadataQuery,
    useUpdateInstitutionMetadataMutation,
    useUpdateInstitutionMutation,
    useCreatePlatformRolesMutation,
    useLazyGetPlatformRolesQuery,
    useGetPlatformRolesQuery,
    useCreatePlatformUserMutation,
    useLazyGetPlatformUserListQuery,
    useGetPlatformUserListQuery,
    useUpdateUserMetadataMutation,
    useDeleteUserFromInstitutionMutation,

    useCreatePlatformFeeMutation,
    useDeletePlatformFeeMutation,
    useEditPlatformFeeMutation,
    useLazyGetAllFeeQuery,
    useGetAllFeeQuery,

    useLazyUseGetAllPlatformsListQuery,

    useGetClientListQuery,
    useGetBillingQuery,
    useUpdateClientExpiryMutation,
    useGetClientDeletePreviewQuery,
    useDeleteClientMutation,

    useGetDatabaseTablesQuery,
    useGetDatabaseTableRowsQuery,
    useDownloadDatabaseExportMutation,
    useImportDatabaseDataMutation
} = settingAction
